import { makeStyles } from '@material-ui/core';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingTop: '0.5rem'
  },
  label: {
    fontSize: '1rem',
    width: '50%'
  },
  inputContainer: {
    width: '100%'
  },
  dateInputContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  dateGroup: {
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  dateLabel: {
    marginBottom: '4px',
    flex: 1
  },
  dateWrapper: {
    display: 'flex',
    gap: '5px',
    flex: 5
  },
  dateInput: {
    border: 'none',
    borderBottom: '1px solid #ccc',
    width: '33.33%',
    padding: '2px 4px',
    fontSize: '14px',
    textAlign: 'center'
  },
  dateInputFocus: {
    outline: 'none',
    borderBottom: '1px solid #000'
  }
});

interface DateInputGroupProps {
  label: string;
  date: { month: string; day: string; year: string };
  onChange: (type: string, value: string) => void;
}

const DateInputGroup = ({ label, date, onChange }: DateInputGroupProps) => {
  const classes = useStyles();
  const [localDate, setLocalDate] = useState(date);

  const updateDate = useDebouncedCallback(() => {
    const { day, month, year } = localDate;
    const date = `${year}-${month}-${day}`;

    if (!moment(date, 'YYYY-MM-DD').isValid()) {
      return;
    }

    onChange('day', day);
    onChange('month', month);
    onChange('year', year);
  }, 500);

  useEffect(() => {
    updateDate();
  }, [localDate.day, localDate.month, localDate.year]);

  useEffect(() => {
    setLocalDate(date);
  }, [date]);

  return (
    <div className={classes.dateGroup}>
      <label className={classes.dateLabel}>{label}</label>
      <div className={classes.dateWrapper}>
        <input
          type="text"
          className={classes.dateInput}
          placeholder="DD"
          maxLength={2}
          value={localDate.day}
          onChange={e => setLocalDate({ ...localDate, day: e.target.value })}
        />
        <span>/</span>
        <input
          type="text"
          className={classes.dateInput}
          placeholder="MM"
          maxLength={2}
          value={localDate.month}
          onChange={e => setLocalDate({ ...localDate, month: e.target.value })}
        />
        <span>/</span>
        <input
          type="text"
          className={classes.dateInput}
          placeholder="YYYY"
          maxLength={4}
          value={localDate.year}
          onChange={e => setLocalDate({ ...localDate, year: e.target.value })}
        />
      </div>
    </div>
  );
};

interface FilterOptionDateProps {
  field: string;
  selectedValue?: any;
  onChange: Function;
}

const FilterOptionDate = ({
  field,
  selectedValue = {},
  onChange
}: FilterOptionDateProps) => {
  const classes = useStyles();
  const [fromDate, setFromDate] = useState({
    day: '',
    month: '',
    year: ''
  });
  const [toDate, setToDate] = useState({ day: '', month: '', year: '' });

  const getFormattedDate = (date: { month: any; day: any; year: any }) => {
    const { day, month, year } = date;
    if (!month || !day || !year) {
      return;
    }
    return `${year}-${month}-${day}`;
  };

  const parseDateString = (dateString: string) => {
    if (!dateString) return { day: '', month: '', year: '' };
    const [year, month, day] = dateString.split('-');
    return { day, month, year };
  };

  useEffect(() => {
    const newMin = getFormattedDate(fromDate);
    const newMax = getFormattedDate(toDate);

    if (
      (selectedValue.min !== newMin && !!newMin) ||
      (selectedValue.max !== newMax && !!newMax)
    ) {
      onChange(field, { min: newMin, max: newMax });
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (selectedValue.min !== getFormattedDate(fromDate)) {
      setFromDate(parseDateString(selectedValue.min));
    }
    if (selectedValue.max !== getFormattedDate(toDate)) {
      setToDate(parseDateString(selectedValue.max));
    }
  }, [selectedValue.min, selectedValue.max]);

  const handleDateChange = (
    type: string,
    value: string,
    range: 'from' | 'to'
  ) => {
    if (range === 'from') {
      setFromDate(prev => ({ ...prev, [type]: value }));
    } else {
      setToDate(prev => ({ ...prev, [type]: value }));
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.inputContainer}>
        <div className={classes.dateInputContainer}>
          <DateInputGroup
            label="From"
            date={fromDate}
            onChange={(type, value) => handleDateChange(type, value, 'from')}
          />
          <DateInputGroup
            label="To"
            date={toDate}
            onChange={(type, value) => handleDateChange(type, value, 'to')}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterOptionDate;
